import { cache as memoizedCache } from 'react';
import { Locale } from '@headless-workspace/config';
import { Result, ResultSuccess, unwrapResultSuccess } from '@headless-workspace/core-domain';
import { CommonDI, ProductsRepositoryType } from '@headless-workspace/domain/common/DI';
import { DataLayerClassName } from '@headless-workspace/domain/common/client';
import {
    EcomConfigKeys,
    getCachedEcomConfigByKey,
    getCachedVipBrands,
    HTMLString,
    PromoText,
    TileProductAvailabilityValue,
    VipBrandsValue,
} from '@headless-workspace/domain/common/server';
import { Image } from '@headless-workspace/typings';
import { PredicateHelpers } from '@headless-workspace/utils';
import { HomeSlotsRepositoryType } from '../../../DI/dependencyInjection';
import { DI } from '../../../DI/index';
import { CTA } from '../types';
import { HomeSlotType } from './HomeSlotType';
import { HomeSlotBase, HomeSlotValue } from './HomeSlotsValue';
import { HomeBeautyTipsValue } from './beautyTips';
import { HomeCarouselValue } from './carousel';

export type HomeProductsRecommendationsValue = HomeSlotBase & {
    type: HomeSlotType.ProductsList | HomeSlotType.ProductRecommendations;
    title: HTMLString;
    cta: CTA | undefined;
    products: TileProductAvailabilityValue[];
};

export type HomeBeautyTipsProductsValue = HomeSlotBase & {
    type: HomeSlotType.BeautyTips;
    subTitle: string;
    contentId: string;
    title: HTMLString;
    image: Image;
    cta: CTA;
    products: TileProductAvailabilityValue[];
};

type ExcludedSlots = HomeCarouselValue | HomeBeautyTipsValue;

export type ReconciledHomeSlotsValue = Exclude<
    HomeSlotValue | HomeProductsRecommendationsValue | HomeBeautyTipsProductsValue,
    ExcludedSlots
>[];

async function fetchProducts(
    locale: Locale,
    productsIds: string[],
    vipBrands: VipBrandsValue,
    promoText: string | undefined,
): Promise<TileProductAvailabilityValue[]> {
    const productsResult = await CommonDI.get(ProductsRepositoryType).getProducts(
        locale,
        productsIds,
        vipBrands,
        promoText,
    );
    return productsResult.type === 'success' ? (productsResult.data ?? []) : [];
}

function mapBeautyTipsSlot(
    slot: HomeBeautyTipsValue,
    products: TileProductAvailabilityValue[],
): HomeBeautyTipsProductsValue {
    return {
        products: products.map((product) => ({
            ...product,
            dataLayer: {
                id: `${slot.contentId}_product-${product.id}`,
                className: DataLayerClassName.beautyTips,
            },
        })),
        ...slot,
    };
}

async function processSlot(
    slot: HomeSlotValue,
    locale: Locale,
    vipBrands: VipBrandsValue,
    promoText: string | undefined,
): Promise<ReconciledHomeSlotsValue[number] | null> {
    if (slot.type === HomeSlotType.ProductRecommendations) {
        const products = await fetchProducts(locale, (slot as HomeCarouselValue).productsIds, vipBrands, promoText);
        return {
            cta: slot.cta,
            products,
            ...slot,
        };
    }

    if (slot.type === HomeSlotType.BeautyTips) {
        const products = await fetchProducts(locale, (slot as HomeBeautyTipsValue).productsIds, vipBrands, promoText);
        return mapBeautyTipsSlot(slot as HomeBeautyTipsValue, products);
    }

    return slot as Exclude<HomeSlotValue, ExcludedSlots>;
}

async function reconcileHomeSlots(locale: Locale): Promise<Result<ReconciledHomeSlotsValue> | undefined> {
    const homeSlotsResult = await DI.get(HomeSlotsRepositoryType).getSlots({ locale, isPreviewable: true });
    const homeSlots = unwrapResultSuccess(homeSlotsResult);

    if (!homeSlots) {
        return undefined;
    }

    const vipBrandsResult = await getCachedVipBrands(locale);
    const vipBrands = unwrapResultSuccess(vipBrandsResult) ?? {};

    const promoTextResult = await getCachedEcomConfigByKey<PromoText>(locale, EcomConfigKeys.PromoStandardText);
    const promoText = unwrapResultSuccess(promoTextResult);

    const processedSlots = await Promise.all(
        homeSlots.map((slot) => processSlot(slot, locale, vipBrands, promoText?.originalPriceText)),
    );

    return ResultSuccess(processedSlots.filter(PredicateHelpers.notNullOrUndefined));
}

export const getMemoHomeSlots = memoizedCache(reconcileHomeSlots);
